import { useEffect, useRef } from "react";
import { GeoJSON } from "react-leaflet";
import type { GeoJSON as LeafletGeoJSON, LatLngBounds, LeafletMouseEvent } from "leaflet";
import { useT } from "../i18n/hooks.ts";
import { approximateGeometryAreaKm2, geometryBounds } from "../model/gisLayer.ts";
import type { MapMitecoMode, MitecoBasinProperties } from "../model/mitecoLayers.ts";
import { MITECO_ALREADY_ADDED_STYLE } from "./mapStyles.ts";
import { RectangleSelectionLayer, type RectangleSelectionMode } from "./RectangleSelection.tsx";
import { escapeHtml } from "./escapeHtml.ts";

const SELECTED_STYLE = { color: "#e0913f", weight: 3, fillOpacity: 0.25, fillColor: "#e0913f" };

/**
 * Shared by MitecoBasinsLayer.tsx/MitecoSubBasinsLayer.tsx — click a
 * polygon (plain toggles; Shift adds/Ctrl removes), or drag a rectangle
 * (Shift adds/Ctrl removes too), same convention as the project's own
 * sub-basin selection in SubBasinMap.tsx, to build up `mode.selected`. Only
 * rendered while the mode is active (see
 * SubBasinsPanel.tsx's "Add from MITECO" button / App.tsx's mitecoMode), so
 * there's no separate "is this visible" concern the way the old
 * always-on LayersControl checkbox version needed.
 */
export function MitecoSelectableLayer({
  data,
  style,
  attribution,
  mode,
}: {
  data: GeoJSON.FeatureCollection;
  style: { color: string; weight: number; fillOpacity: number };
  attribution: string;
  mode: MapMitecoMode;
}) {
  const t = useT();
  const layerRef = useRef<LeafletGeoJSON | null>(null);
  const selectedKeys = Array.from(mode.selected.keys()).join(",");

  // Repaints the selection highlight — same reason/technique as
  // SubBasinMap.tsx's own subBasinsLayerRef effect: Leaflet's setStyle
  // updates the existing <path>s in place, no need to touch onEachFeature's
  // click handlers for a plain (de)selection. Also re-runs when `data`
  // changes: that forces the <GeoJSON> below to remount (see its own `key`
  // comment), which resets every feature back to the plain base `style` —
  // without this, already-selected features loaded before a new region
  // would silently lose their highlight the next time one arrives.
  useEffect(() => {
    layerRef.current?.setStyle((feature) => {
      const props = feature?.properties as MitecoBasinProperties;
      if (mode.alreadyAdded.has(props.basinCode)) return MITECO_ALREADY_ADDED_STYLE;
      return mode.selected.has(props.basinCode) ? SELECTED_STYLE : style;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKeys, style, data, mode.alreadyAdded]);

  // Already-added features (mode.alreadyAdded) don't toggle at all — there's
  // nothing to add, and letting them into `selected` would just get filtered
  // back out at commit time anyway (addSelectedMiteco, App.tsx). Plain click
  // toggles; Shift+click adds, Ctrl+click removes — same semantics as
  // Shift+drag/Ctrl+drag below (see the equivalent handler in
  // SubBasinMap.tsx/AssignmentMap.tsx).
  function toggle(feature: GeoJSON.Feature, e: LeafletMouseEvent) {
    const props = feature.properties as MitecoBasinProperties;
    if (mode.alreadyAdded.has(props.basinCode)) return;
    const next = new Map(mode.selected);
    if (e.originalEvent.shiftKey) next.set(props.basinCode, feature);
    else if (e.originalEvent.ctrlKey || e.originalEvent.metaKey) next.delete(props.basinCode);
    else if (next.has(props.basinCode)) next.delete(props.basinCode);
    else next.set(props.basinCode, feature);
    mode.onChangeSelected(next);
  }

  function selectByRectangle(bounds: LatLngBounds, rectangleMode: RectangleSelectionMode) {
    const next = new Map(mode.selected);
    for (const f of data.features) {
      const featureBounds = geometryBounds(f.geometry);
      if (!featureBounds) continue;
      if (!bounds.intersects([featureBounds[0], featureBounds[1]])) continue;
      const props = f.properties as MitecoBasinProperties;
      if (mode.alreadyAdded.has(props.basinCode)) continue;
      if (rectangleMode === "add") next.set(props.basinCode, f);
      else next.delete(props.basinCode);
    }
    mode.onChangeSelected(next);
  }

  return (
    <>
      {/* key: react-leaflet's <GeoJSON> only ever reads `data` at creation
          time (see node_modules/react-leaflet/lib/GeoJSON.js's updateGeoJSON
          — it syncs `style` on prop change, nothing else) — changing the
          `data` prop on an already-mounted instance is silently ignored.
          MitecoSubBasinsLayer.tsx's `data` genuinely grows over time (more
          regions fetched while panning), so it needs a real remount to pick
          that up; features.length is enough to detect that here without
          this component needing to know anything about *why* data changed. */}
      <GeoJSON
        key={data.features.length}
        ref={layerRef}
        data={data}
        style={style}
        attribution={attribution}
        onEachFeature={(feature, layer) => {
          const props = feature.properties as MitecoBasinProperties;
          const areaKm2 = approximateGeometryAreaKm2(feature.geometry);
          const alreadyAdded = mode.alreadyAdded.has(props.basinCode);
          layer.bindTooltip(
            `<strong>${escapeHtml(props.name)}</strong><br/>${escapeHtml(t("map.area", { km2: areaKm2.toFixed(1) }))}` +
              (alreadyAdded
                ? `<br/><em>${escapeHtml(t("subBasinsPanel.mitecoAlreadyAdded"))}</em>`
                : ""),
            { sticky: true, direction: "top" }
          );
          layer.on("click", (e) => toggle(feature, e));
        }}
      />
      <RectangleSelectionLayer onSelect={selectByRectangle} />
    </>
  );
}
